/**
 * The in-session transcript the voice window renders: what you said, what the
 * assistant said, the work it did in between, and the session events that
 * matter to the caller (a barge-in, a dropped connection).
 *
 * A pure reducer over the hook's frames, kept apart from chat history on
 * purpose. History is owned by `use-voice-mode-handlers` and pairs answers to
 * committed turns for persistence; this model only has to read well while the
 * call runs, so it can show the live line before a turn is committed and keep
 * an interrupted answer with its cut mark.
 */
import type { ActivityMessage } from "@/api/models"

export type VoiceTranscriptEntry =
  | {
      kind: "you"
      id: string
      text: string
      /** Speech in progress: not yet a committed turn. */
      live: boolean
      at: number
    }
  | {
      kind: "ai"
      id: string
      text: string
      streaming: boolean
      /** The caller spoke over this answer; the text is what was heard. */
      interrupted: boolean
      at: number
    }
  | {
      kind: "work"
      id: string
      /** Tool name when known; empty for a plain activity line. */
      name: string
      /** The activity the chat translators render, or a synthesized one for a tool call. */
      activity: ActivityMessage
      status: "running" | "done" | "failed" | "aborted"
      at: number
    }
  | {
      kind: "system"
      id: string
      code: "connection_lost" | "closed"
      at: number
    }

export type VoiceTranscriptEvent =
  | { type: "user_live"; text: string }
  | { type: "user_committed"; text: string }
  | { type: "assistant_delta"; text: string }
  | { type: "assistant_done"; text: string }
  | { type: "assistant_interrupted" }
  | { type: "work_started"; name: string; callId?: string; activity: ActivityMessage }
  | { type: "work_finished"; name: string; callId?: string; success: boolean }
  | { type: "work_aborted"; name: string; callId?: string }
  | { type: "activity"; activity: ActivityMessage }
  /** Whatever was running is over without a terminal of its own (watchdog, reconnect, dead run). */
  | { type: "work_settle" }
  | { type: "system"; code: "connection_lost" | "closed" }
  | { type: "reset" }

/**
 * Voice-only plumbing the caller should never read as a step: the delegation
 * umbrella and the turn-based poll. Their work reaches the transcript through
 * the activity lines they produce and the row's working state.
 */
const VOICE_PLUMBING_TOOLS = new Set(["work_in_chat", "check_background_work"])

export function isVoicePlumbingTool(name: string): boolean {
  return VOICE_PLUMBING_TOOLS.has(name)
}

/** Keep the model bounded; a call is capped at 20 minutes and nobody reads back further. */
export const VOICE_TRANSCRIPT_LIMIT = 400

let sequence = 0
function nextId(prefix: string): string {
  sequence += 1
  return `${prefix}-${sequence}`
}

/**
 * The newest streaming answer, looking back past work lines and past the
 * caller's own live line (on a full-duplex call you can talk over an answer
 * without ending it). A committed caller turn does end the search.
 */
function findStreamingAnswer(entries: VoiceTranscriptEntry[]): number {
  for (let index = entries.length - 1; index >= 0; index -= 1) {
    const entry = entries[index]
    if (entry.kind === "you" && !entry.live) return -1
    if (entry.kind === "ai" && entry.streaming) return index
  }
  return -1
}

/**
 * The answer a barge-in already closed, still in the turn in progress. The
 * Live route interrupts first and sends the answer's final text afterwards.
 */
function findInterruptedAnswer(entries: VoiceTranscriptEntry[]): number {
  for (let index = entries.length - 1; index >= 0; index -= 1) {
    const entry = entries[index]
    if (entry.kind === "you" && !entry.live) return -1
    if (entry.kind === "ai") return entry.interrupted ? index : -1
  }
  return -1
}

/** The caller's live line, wherever it sits after the last committed turn. */
function findLiveLine(entries: VoiceTranscriptEntry[]): number {
  for (let index = entries.length - 1; index >= 0; index -= 1) {
    const entry = entries[index]
    if (entry.kind !== "you") continue
    return entry.live ? index : -1
  }
  return -1
}

function bounded(entries: VoiceTranscriptEntry[]): VoiceTranscriptEntry[] {
  return entries.length > VOICE_TRANSCRIPT_LIMIT
    ? entries.slice(entries.length - VOICE_TRANSCRIPT_LIMIT)
    : entries
}

function findWork(
  entries: VoiceTranscriptEntry[],
  name: string,
  callId: string | undefined
): number {
  for (let index = entries.length - 1; index >= 0; index -= 1) {
    const entry = entries[index]
    if (entry.kind !== "work" || entry.status !== "running") continue
    if (callId ? entry.id === `work-${callId}` : entry.name === name) return index
  }
  return -1
}

/** Apply one event. Never mutates; returns the same array when nothing changed. */
export function reduceVoiceTranscript(
  entries: VoiceTranscriptEntry[],
  event: VoiceTranscriptEvent,
  now: number
): VoiceTranscriptEntry[] {
  switch (event.type) {
    case "reset":
      return entries.length === 0 ? entries : []

    case "user_live": {
      const text = event.text
      if (!text) return entries
      const index = findLiveLine(entries)
      if (index >= 0) {
        const entry = entries[index]
        if (entry.kind === "you" && entry.text === text) return entries
        const updated = [...entries]
        updated[index] = { ...entry, text } as VoiceTranscriptEntry
        return updated
      }
      return bounded([...entries, { kind: "you", id: nextId("you"), text, live: true, at: now }])
    }

    case "user_committed": {
      const text = event.text.trim()
      if (!text) return entries
      // The answer may already have started (deferred caller turns): the line
      // is finalized where it was spoken, not appended after the answer.
      const index = findLiveLine(entries)
      if (index >= 0) {
        const entry = entries[index]
        const updated = [...entries]
        updated[index] = { ...entry, text, live: false } as VoiceTranscriptEntry
        return updated
      }
      return bounded([...entries, { kind: "you", id: nextId("you"), text, live: false, at: now }])
    }

    case "assistant_delta": {
      if (!event.text) return entries
      // A work line or the caller's live line between two segments of one
      // answer must not split the answer.
      const index = findStreamingAnswer(entries)
      if (index >= 0) {
        const entry = entries[index] as Extract<VoiceTranscriptEntry, { kind: "ai" }>
        const updated = [...entries]
        updated[index] = { ...entry, text: entry.text + event.text }
        return updated
      }
      return bounded([
        ...entries,
        {
          kind: "ai",
          id: nextId("ai"),
          text: event.text,
          streaming: true,
          interrupted: false,
          at: now,
        },
      ])
    }

    case "assistant_done": {
      const index = findStreamingAnswer(entries)
      if (index >= 0) {
        const entry = entries[index] as Extract<VoiceTranscriptEntry, { kind: "ai" }>
        const updated = [...entries]
        updated[index] = { ...entry, text: event.text || entry.text, streaming: false }
        return updated
      }
      // A barge-in closed this answer before its final text arrived: the text
      // belongs to that entry, cut mark included, not to a second one.
      const interruptedIndex = findInterruptedAnswer(entries)
      if (interruptedIndex >= 0) {
        const entry = entries[interruptedIndex] as Extract<VoiceTranscriptEntry, { kind: "ai" }>
        if (!event.text || event.text === entry.text) return entries
        const updated = [...entries]
        updated[interruptedIndex] = { ...entry, text: event.text }
        return updated
      }
      // An answer that arrived whole (no deltas) still belongs on the record.
      if (!event.text.trim()) return entries
      return bounded([
        ...entries,
        {
          kind: "ai",
          id: nextId("ai"),
          text: event.text,
          streaming: false,
          interrupted: false,
          at: now,
        },
      ])
    }

    case "assistant_interrupted": {
      const index = findStreamingAnswer(entries)
      if (index < 0) return entries
      const entry = entries[index] as Extract<VoiceTranscriptEntry, { kind: "ai" }>
      const updated = [...entries]
      updated[index] = { ...entry, streaming: false, interrupted: true }
      return updated
    }

    case "work_started": {
      const id = event.callId ? `work-${event.callId}` : nextId("work")
      // The same call is announced twice on the wire (`tool_call_started`,
      // then `tool_executing`); it is one step on the record.
      if (findWork(entries, event.name, event.callId) >= 0) return entries
      return bounded([
        ...entries,
        {
          kind: "work",
          id,
          name: event.name,
          activity: event.activity,
          status: "running",
          at: now,
        },
      ])
    }

    case "work_finished":
    case "work_aborted": {
      const index = findWork(entries, event.name, event.callId)
      if (index < 0) return entries
      const entry = entries[index] as Extract<VoiceTranscriptEntry, { kind: "work" }>
      let status: "done" | "failed" | "aborted" = "aborted"
      if (event.type === "work_finished") status = event.success ? "done" : "failed"
      const updated = [...entries]
      updated[index] = { ...entry, status }
      return updated
    }

    case "work_settle": {
      if (!entries.some((entry) => entry.kind === "work" && entry.status === "running")) {
        return entries
      }
      return entries.map((entry) =>
        entry.kind === "work" && entry.status === "running"
          ? { ...entry, status: "aborted" as const }
          : entry
      )
    }

    case "activity": {
      return bounded([
        ...entries,
        {
          kind: "work",
          id: nextId("act"),
          name: "",
          activity: event.activity,
          status: "done",
          at: now,
        },
      ])
    }

    case "system":
      return bounded([...entries, { kind: "system", id: nextId("sys"), code: event.code, at: now }])

    default:
      return entries
  }
}

/** Work entries that belong to the answer in progress: everything after the last committed turn. */
export function currentTurnWork(
  entries: VoiceTranscriptEntry[]
): Extract<VoiceTranscriptEntry, { kind: "work" }>[] {
  const work: Extract<VoiceTranscriptEntry, { kind: "work" }>[] = []
  for (let index = entries.length - 1; index >= 0; index -= 1) {
    const entry = entries[index]
    if (entry.kind === "you" && !entry.live) break
    if (entry.kind === "work") work.unshift(entry)
  }
  return work
}

/** Every work entry of the session, oldest first. */
export function allWork(
  entries: VoiceTranscriptEntry[]
): Extract<VoiceTranscriptEntry, { kind: "work" }>[] {
  return entries.filter(
    (entry): entry is Extract<VoiceTranscriptEntry, { kind: "work" }> => entry.kind === "work"
  )
}
